import MuiSelect, { type SelectProps } from "@mui/material/Select";
import MuiMenuItem from "@mui/material/MenuItem";
import MuiCheckbox from "@mui/material/Checkbox";
import MuiListItemText from "@mui/material/ListItemText";
import MuiFormControl from "@mui/material/FormControl";
import MuiInputLabel from "@mui/material/InputLabel";
import MuiButton from "@mui/material/Button";
import { type SelectChangeEvent } from "@mui/material/Select";

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectProps
  extends Omit<SelectProps<string[]>, "multiple" | "value" | "onChange" | "renderValue"> {
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  selectAll?: boolean;
  selectAllLabel?: string;
}

export function MultiSelect({
  options,
  value,
  onChange,
  selectAll = true,
  selectAllLabel = "Select All",
  label,
  ...rest
}: MultiSelectProps) {
  const allSelected = options.length > 0 && value.length === options.length;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const selected = event.target.value;
    const arr = typeof selected === "string" ? selected.split(",") : selected;
    onChange(arr);
  };

  const handleSelectAll = () => {
    onChange(allSelected ? [] : options.map((o) => o.value));
  };

  const labelMap = new Map(options.map((o) => [o.value, o.label]));

  const renderValue = (selected: string[]) =>
    selected.map((v) => labelMap.get(v) ?? v).join(", ");

  const labelId = label
    ? `forest-multiselect-${String(label).replace(/\s+/g, "-").toLowerCase()}`
    : undefined;

  return (
    <MuiFormControl
      fullWidth={rest.fullWidth}
      disabled={rest.disabled}
      error={rest.error}
      size={rest.size}
      color={rest.color as any}
      variant={rest.variant as any}
    >
      {label && (
        <MuiInputLabel id={labelId} shrink={value.length > 0}>
          {label}
        </MuiInputLabel>
      )}
      <MuiSelect<string[]>
        labelId={labelId}
        label={label}
        multiple
        notched={value.length > 0}
        value={value}
        onChange={handleChange}
        displayEmpty
        renderValue={renderValue}
        {...rest}
      >
        {selectAll && (
          <li
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectAll();
            }}
          >
            <MuiButton size="small" variant="text">
              {allSelected ? "Deselect All" : selectAllLabel}
            </MuiButton>
          </li>
        )}
        {options.map((option) => (
          <MuiMenuItem key={option.value} value={option.value}>
            <MuiCheckbox checked={value.includes(option.value)} />
            <MuiListItemText primary={option.label} />
          </MuiMenuItem>
        ))}
      </MuiSelect>
    </MuiFormControl>
  );
}
